<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import { podcastInquiryFormSchema } from "@fergmar/contracts/public/forms/podcast-inquiry-form.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { Textarea } from "#lib/web-ui/ui/textarea/index.js";
	import { toast } from "svelte-sonner";
	import { formatPhoneNumber } from "#lib/web-ui/helpers.js";
	import { dev } from "$app/env";
	import { TurnstileField } from "./turnstile";
	import CheckCircleIcon from "@lucide/svelte/icons/check-circle";

	let submitted = $state(false);

	const form = superForm(defaults(zod4(podcastInquiryFormSchema)), {
		id: "podcast-inquiry",
		SPA: true,
		resetForm: true,
		validators: zod4(podcastInquiryFormSchema),
		async onUpdate({ form }) {
			if (!form.valid) return;

			try {
				await trpcBrowser.public.forms.submitPodcastInquiry.mutate(form.data);
			} catch (e) {
				form.data.turnstileToken = "";
				toast.error(e instanceof Error ? e.message : "Failed to submit. Please try again.");
				return;
			}

			submitted = true;
			toast.success("Podcast inquiry submitted!");
		}
	});

	const { form: formData, enhance, errors, submitting } = form;

	if (dev) {
		$formData.firstName = "John";
		$formData.lastName = "Doe";
		$formData.email = "john.doe@example.com";
		$formData.phone = "(123) 456-7890";
		$formData.podcastName = "The Real Estate Show";
		$formData.podcastUrl = "https://example.com/podcast";
		$formData.message =
			"I'd love to have Brian on my podcast to discuss real estate investing.";
	}
</script>

{#if submitted}
	<div class="flex flex-col items-center gap-4 py-8 text-center">
		<CheckCircleIcon class="text-primary h-12 w-12" />
		<h4 class="text-foreground text-lg font-semibold">Thank you for reaching out!</h4>
		<p class="text-muted-foreground max-w-sm text-sm">
			We've received your podcast inquiry and will get back to you shortly.
		</p>
	</div>
{:else}
	<form use:enhance method="POST" class="w-full space-y-4">
		<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<div class="space-y-2">
				<Label for="pi-firstName">First Name</Label>
				<Input
					name="firstName"
					id="pi-firstName"
					type="text"
					bind:value={$formData.firstName}
					autocomplete="given-name"
				/>
				{#if $errors.firstName}
					<p class="text-destructive text-sm">{$errors.firstName}</p>
				{/if}
			</div>

			<div class="space-y-2">
				<Label for="pi-lastName">Last Name</Label>
				<Input
					name="lastName"
					id="pi-lastName"
					type="text"
					bind:value={$formData.lastName}
					autocomplete="family-name"
				/>
				{#if $errors.lastName}
					<p class="text-destructive text-sm">{$errors.lastName}</p>
				{/if}
			</div>
		</div>

		<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<div class="space-y-2">
				<Label for="pi-email">Email</Label>
				<Input
					name="email"
					id="pi-email"
					type="email"
					bind:value={$formData.email}
					autocomplete="email"
				/>
				{#if $errors.email}
					<p class="text-destructive text-sm">{$errors.email}</p>
				{/if}
			</div>

			<div class="space-y-2">
				<Label for="pi-phone">Phone</Label>
				<Input
					name="phone"
					id="pi-phone"
					type="tel"
					bind:value={$formData.phone}
					placeholder="(123) 456-7890"
					autocomplete="tel"
					onchange={(e) => {
						$formData.phone = formatPhoneNumber(e.currentTarget.value);
					}}
				/>
				{#if $errors.phone}
					<p class="text-destructive text-sm">{$errors.phone}</p>
				{/if}
			</div>
		</div>

		<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<div class="space-y-2">
				<Label for="pi-podcastName">Podcast Name</Label>
				<Input
					name="podcastName"
					id="pi-podcastName"
					type="text"
					bind:value={$formData.podcastName}
					placeholder="Your podcast name"
				/>
				{#if $errors.podcastName}
					<p class="text-destructive text-sm">{$errors.podcastName}</p>
				{/if}
			</div>

			<div class="space-y-2">
				<Label for="pi-podcastUrl">
					Podcast URL
					<span class="text-muted-foreground text-xs">(optional)</span>
				</Label>
				<Input
					name="podcastUrl"
					id="pi-podcastUrl"
					type="url"
					bind:value={$formData.podcastUrl}
					placeholder="https://yourpodcast.com"
				/>
				{#if $errors.podcastUrl}
					<p class="text-destructive text-sm">{$errors.podcastUrl}</p>
				{/if}
			</div>
		</div>

		<div class="space-y-2">
			<Label for="pi-message">Message</Label>
			<Textarea
				class="bg-background w-full"
				name="message"
				id="pi-message"
				bind:value={$formData.message}
				rows={3}
				placeholder="Tell us about your podcast and what topics you'd like to discuss with Brian..."
			/>
			{#if $errors.message}
				<p class="text-destructive text-sm">{$errors.message}</p>
			{/if}
		</div>

		<div>
			<TurnstileField bind:token={$formData.turnstileToken} class="mb-2" />
		</div>

		<Button
			type="submit"
			disabled={$submitting}
			aria-busy={$submitting}
			class="bg-primary text-primary-foreground hover:bg-primary/80 h-10 w-full rounded text-base font-semibold"
		>
			{$submitting ? "Submitting..." : "Submit Inquiry"}
		</Button>
	</form>
{/if}
